<template>
  <div class="card-chart">
    <!-- Card Header -->
    <div class="card-header">
      <div class="card-header-left">
        <h3 class="card-title">{{ title }}</h3>
        <span v-if="subtitle" class="card-subtitle">{{ subtitle }}</span>
      </div>
      <div class="card-header-right">
        <slot name="controls"></slot>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">
      <div class="chart-container" ref="chartContainer" @mousemove="handleMouseMove" @click="handleContainerClick">
        <v-chart
          v-if="computedChartOptions"
          ref="chart"
          :option="computedChartOptions"
          :autoresize="true"
          @click="handleChartClick"
        />
        <div v-else class="chart-empty">No data available</div>
      </div>
    </div>
  </div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components';
import VChart from 'vue-echarts';

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
]);

export default {
  name: 'CardLineChart',
  components: { VChart },
  emits: ['point-click'],
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    data: {
      type: Object,
      default: null
    },
    variant: {
      type: String,
      default: 'timeseries',
      validator: (v) => ['timeseries', 'cumulative', 'multiline', 'stacked'].includes(v)
    },
    options: {
      type: Object,
      default: () => ({})
    },
    height: {
      type: Number,
      default: 300
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    tension: {
      type: Number,
      default: 0.4
    },
    formatTooltipValue: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      currentDataY: null,
      clickHandledByChart: false
    };
  },
  computed: {
    shouldFill() {
      return this.variant === 'cumulative' || this.variant === 'stacked';
    },
    isStacked() {
      return this.variant === 'stacked';
    },
    isActuallyStacked() {
      // Check if stacked via variant or via options.series with stack property
      if (this.isStacked) return true;
      if (this.options?.series) {
        const series = Array.isArray(this.options.series) ? this.options.series : [this.options.series];
        return series.some(s => s.stack);
      }
      return false;
    },
    computedChartOptions() {
      if (!this.data || !this.data.labels || !this.data.datasets || this.data.datasets.length === 0) {
        return null;
      }

      // Primary indigo palette - ordered for contrast in stacked charts (dark/light alternating)
      const defaultColors = ['#4338ca', '#a5b4fc', '#3730a3', '#c7d2fe', '#4f46e5', '#818cf8', '#312e81', '#6366f1'];
      // Fill colors for cumulative charts - all indigo-based
      const defaultFillColors = [
        'rgba(67, 56, 202, 0.2)',
        'rgba(165, 180, 252, 0.2)',
        'rgba(55, 48, 163, 0.2)',
        'rgba(199, 210, 254, 0.2)',
        'rgba(79, 70, 229, 0.2)'
      ];

      // Extract colors from datasets or use defaults
      const colors = this.data.datasets.map((dataset, index) =>
        dataset.color || defaultColors[index % defaultColors.length]
      );

      const series = this.data.datasets.map((dataset, index) => {
        const color = colors[index];
        const fillColor = dataset.fillColor || defaultFillColors[index % defaultFillColors.length];

        return {
          name: dataset.label,
          type: 'line',
          data: dataset.data,
          smooth: this.tension > 0,
          showSymbol: true,
          symbol: 'circle',
          symbolSize: 8,
          stack: this.isStacked ? 'total' : undefined,
          lineStyle: {
            width: 2,
            color: color
          },
          itemStyle: {
            color: color,
            borderColor: '#fff',
            borderWidth: 2
          },
          areaStyle: this.shouldFill ? {
            color: this.isStacked ? color : {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: fillColor },
                { offset: 1, color: 'rgba(255, 255, 255, 0)' }
              ]
            },
            opacity: this.isStacked ? 0.8 : 1
          } : undefined,
          emphasis: {
            focus: 'series',
            itemStyle: {
              borderWidth: 3
            }
          },
          triggerLineEvent: true
        };
      });

      const self = this;

      const baseOptions = {
        color: colors,
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: 'transparent',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          padding: 12,
          formatter: this.isActuallyStacked
            ? (params) => self.formatStackedTooltip(params)
            : this.formatTooltipValue
              ? (params) => {
                  const items = Array.isArray(params) ? params : [params];
                  if (items.length === 0) return '';
                  let html = `<div style="font-weight: 600; margin-bottom: 8px;">${items[0].name}</div>`;
                  items.forEach(p => {
                    const value = p.value || 0;
                    const formattedValue = self.formatTooltipValue(value);
                    html += `
                      <div style="display: flex; align-items: center; gap: 8px; margin: 4px 0;">
                        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${p.color};"></span>
                        <span>${p.seriesName}: ${formattedValue}</span>
                      </div>
                    `;
                  });
                  return html;
                }
              : undefined
        },
        legend: {
          show: this.showLegend && (this.variant === 'multiline' || this.data.datasets.length > 1),
          bottom: 0,
          left: 'center',
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: {
            fontSize: 12,
            color: '#666'
          }
        },
        grid: {
          left: 12,
          right: 12,
          bottom: this.showLegend && (this.variant === 'multiline' || this.data.datasets.length > 1) ? 40 : 12,
          top: 12,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.data.labels,
          boundaryGap: false,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#666',
            fontSize: 11
          }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#666',
            fontSize: 11
          },
          splitLine: {
            lineStyle: { color: '#f0f0f0' }
          }
        },
        series
      };

      // Merge with user options, but preserve our stacked tooltip formatter
      if (!this.options || Object.keys(this.options).length === 0) {
        return baseOptions;
      }

      const merged = this.deepMerge(baseOptions, this.options);

      // For stacked charts, always use our formatter for Y-detection
      if (this.isActuallyStacked) {
        merged.tooltip.trigger = 'axis';
        merged.tooltip.formatter = (params) => self.formatStackedTooltip(params);
      }

      return merged;
    }
  },
  methods: {
    handleMouseMove(event) {
      if (!this.isActuallyStacked) return;

      const chart = this.$refs.chart;
      if (!chart) return;

      const echartsInstance = chart.chart;
      if (!echartsInstance) return;

      const rect = this.$refs.chartContainer.getBoundingClientRect();
      const pixelY = event.clientY - rect.top;

      try {
        const point = echartsInstance.convertFromPixel('grid', [0, pixelY]);
        if (point && point[1] !== undefined) {
          this.currentDataY = point[1];
        }
      } catch (e) {
        // Chart not ready yet
      }
    },

    formatStackedTooltip(params) {
      if (!Array.isArray(params) || params.length === 0) return '';

      // Find which series is hovered based on Y position
      const hoveredParam = this.findHoveredSeries(params);
      if (!hoveredParam) return '';

      const value = hoveredParam.value || 0;
      const formattedValue = this.formatTooltipValue
        ? this.formatTooltipValue(value)
        : value.toLocaleString();

      return `
        <div style="font-weight: 600; margin-bottom: 8px;">${hoveredParam.name}</div>
        <div style="display: flex; align-items: center; gap: 8px; margin: 4px 0;">
          <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${hoveredParam.color};"></span>
          <span>${hoveredParam.seriesName}: ${formattedValue}</span>
        </div>
      `;
    },

    findHoveredSeries(params) {
      // If we don't have Y position yet, return topmost series
      if (this.currentDataY === null) {
        return params[params.length - 1];
      }

      // For stacked charts, calculate cumulative values to find which "band" we're in
      // Series are stacked from bottom to top, so we iterate in order
      let cumulative = 0;

      for (let i = 0; i < params.length; i++) {
        const value = params[i].value || 0;
        const nextCumulative = cumulative + value;

        // Check if currentDataY falls within this band
        if (this.currentDataY >= cumulative && this.currentDataY <= nextCumulative) {
          return params[i];
        }
        cumulative = nextCumulative;
      }

      // If above all stacks, return the topmost series
      if (this.currentDataY > cumulative) {
        return params[params.length - 1];
      }

      // If below zero (shouldn't happen for positive data), return bottom series
      return params[0];
    },

    handleChartClick(params) {
      // Mark that this click was handled by the chart
      this.clickHandledByChart = true;
      setTimeout(() => { this.clickHandledByChart = false; }, 100);

      if (params.componentType === 'series') {
        // For stacked charts, ALWAYS use position-based detection
        if (this.isActuallyStacked) {
          const chart = this.$refs.chart;
          if (chart && chart.chart) {
            const echartsInstance = chart.chart;

            // Get pixel position from the ECharts event
            const event = params.event?.event || params.event;
            if (event) {
              const rect = this.$refs.chartContainer.getBoundingClientRect();
              const pixelX = (event.clientX || event.zrX || 0) - rect.left;
              const pixelY = (event.clientY || event.zrY || 0) - rect.top;

              try {
                const point = echartsInstance.convertFromPixel('grid', [pixelX, pixelY]);
                if (point && point[1] !== undefined) {
                  this.currentDataY = point[1];
                }
              } catch (e) {
                // Use tracked mouse position as fallback
              }
            }

            // Get all series data at this x index
            const dataIndex = params.dataIndex;
            const allParams = this.data.datasets.map((dataset, seriesIndex) => ({
              seriesName: dataset.label,
              value: dataset.data[dataIndex] || 0,
              color: dataset.color || this.computedChartOptions?.color?.[seriesIndex],
              dataIndex: dataIndex,
              name: this.data.labels[dataIndex]
            }));

            // Find which series band the click was in
            if (this.currentDataY !== null) {
              const hoveredParam = this.findHoveredSeries(allParams);
              if (hoveredParam) {
                this.$emit('point-click', {
                  dataIndex: hoveredParam.dataIndex,
                  seriesName: hoveredParam.seriesName,
                  value: hoveredParam.value,
                  name: hoveredParam.name
                });
                return;
              }
            }
          }
        }

        // Default behavior for non-stacked or if Y detection fails
        this.$emit('point-click', {
          dataIndex: params.dataIndex,
          seriesName: params.seriesName,
          value: params.value,
          name: params.name
        });
      }
    },

    handleContainerClick(event) {
      // Only handle clicks that weren't caught by ECharts (clicks outside series elements)
      if (this.clickHandledByChart) return;
      if (!this.isActuallyStacked) return;

      const chart = this.$refs.chart;
      if (!chart || !chart.chart) return;

      const echartsInstance = chart.chart;
      const rect = this.$refs.chartContainer.getBoundingClientRect();
      const pixelX = event.clientX - rect.left;
      const pixelY = event.clientY - rect.top;

      try {
        // Convert pixel position to data position
        const point = echartsInstance.convertFromPixel('grid', [pixelX, pixelY]);
        if (!point || point[0] === undefined || point[1] === undefined) return;

        const dataX = point[0];
        const dataY = point[1];

        // Find the closest X index (period)
        const dataIndex = Math.round(dataX);
        if (dataIndex < 0 || dataIndex >= this.data.labels.length) return;

        // Build params for all series at this X index
        const allParams = this.data.datasets.map((dataset, seriesIndex) => ({
          seriesName: dataset.label,
          value: dataset.data[dataIndex] || 0,
          color: dataset.color || this.computedChartOptions?.color?.[seriesIndex],
          dataIndex: dataIndex,
          name: this.data.labels[dataIndex]
        }));

        // Use the dataY to find which series band was clicked
        this.currentDataY = dataY;
        const hoveredParam = this.findHoveredSeries(allParams);

        if (hoveredParam) {
          this.$emit('point-click', {
            dataIndex: hoveredParam.dataIndex,
            seriesName: hoveredParam.seriesName,
            value: hoveredParam.value,
            name: hoveredParam.name
          });
        }
      } catch (e) {
        // Chart not ready or click outside grid area
      }
    },
    deepMerge(target, source) {
      const output = { ...target };
      if (this.isObject(target) && this.isObject(source)) {
        Object.keys(source).forEach(key => {
          if (this.isObject(source[key])) {
            if (!(key in target)) {
              Object.assign(output, { [key]: source[key] });
            } else {
              output[key] = this.deepMerge(target[key], source[key]);
            }
          } else {
            Object.assign(output, { [key]: source[key] });
          }
        });
      }
      return output;
    },
    isObject(item) {
      return item && typeof item === 'object' && !Array.isArray(item);
    }
  }
}
</script>

<style scoped>
.card-chart {
  background: #FFF;
  overflow: hidden;
}

.card-header {
  padding: 16px;
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header-left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.card-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.card-subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #666;
}

.card-body {
  padding: 16px;
}

.chart-container {
  position: relative;
  height: v-bind('height + "px"');
}

.chart-container .v-chart {
  width: 100%;
  height: 100%;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  font-size: 14px;
}
</style>
